import * as dates from "@client/dates";
import * as planner from "@client/features/planner";
import * as profile from "@client/features/profile";
import {server} from "@reactivated";
import React from "react";

export type Activity = "SEDENTARY" | "ACTIVE" | "VERY_ACTIVE";
export type Goal = "LOSE" | "MAINTAIN" | "GAIN";

export type Body = ReturnType<typeof profile.useProfile>;

export type Macros = {
    calories: number;
    protein: number;
    fat: number;
    carbs: number;
};

const DEMO_BODY: NonNullable<Parameters<typeof profile.useProfile>[0]>["initial"] = {
    gender: "MALE",
    birth_month: "05",
    birth_day: "15",
    birth_year: "1990",
    date_of_birth: "1990-05-15",
    feet: "5",
    inches: "10",
    weightLb: "175",
};

export const ACTIVITY_OPTIONS: {value: Activity; title: string; hint: string[]}[] = [
    {
        value: "SEDENTARY",
        title: "Sedentary",
        hint: ["You sit most of the day.", "You don't walk much."],
    },
    {
        value: "ACTIVE",
        title: "Moderately active",
        hint: [
            "You're on your feet most of the day.",
            "Your job involves a lot of walking.",
        ],
    },
    {
        value: "VERY_ACTIVE",
        title: "Very active",
        hint: ["Physical work, like construction.", "You're moving all day."],
    },
];

export const GOAL_OPTIONS: {value: Goal; title: string; hint: string[]}[] = [
    {
        value: "LOSE",
        title: "Lose fat",
        hint: [
            "Lose at most a pound a week.",
            "Mostly from fat. Sustainable long term.",
        ],
    },
    {
        value: "MAINTAIN",
        title: "Maintain",
        hint: ["Get healthier.", "Slowly replace fat with muscle."],
    },
    {
        value: "GAIN",
        title: "Gain muscle",
        hint: ["Put on muscle with minimal fat.", "Slow and steady."],
    },
];

const ACTIVITY_MULTIPLIER: Record<Activity, string> = {
    SEDENTARY: "1.2",
    ACTIVE: "1.375",
    VERY_ACTIVE: "1.55",
};

const GOAL_VARIANCE: Record<Goal, string> = {
    LOSE: "-20",
    MAINTAIN: "0",
    GAIN: "15",
};

const PROTEIN_MULTIPLIER = "1.75";
const FAT_CARBS = "0.50";

const parseNumber = (raw: string | null) => {
    const value = parseFloat(raw ?? "");
    return Number.isFinite(value) && value > 0 ? value : null;
};

const weightKg = (values: Body["values"]) => {
    if (values.units === "METRIC") {
        return parseNumber(values.weightKg);
    }
    const pounds = parseNumber(values.weightLb);
    return pounds == null ? null : pounds * 0.45359237;
};

const ageYears = (dateOfBirth: string | null) => {
    if (dateOfBirth == null) {
        return null;
    }
    const parsed = dates.parseISO(dateOfBirth);
    if (!dates.isValid(parsed)) {
        return null;
    }
    const age = dates.differenceInYears(new Date(), parsed);
    return age >= 13 && age <= 100 ? age : null;
};

export const isAboutComplete = (body: Body) =>
    body.values.gender != null && ageYears(body.values.date_of_birth) != null;

export const isBodyComplete = (body: Body) =>
    parseNumber(body.normalizedHeight) != null && weightKg(body.values) != null;

// The wizard's answers, run through the general calculator the planner uses.
export const computeMacros = (
    body: Body,
    activity: Activity | null,
    goal: Goal | null,
): Macros | null => {
    const height = parseNumber(body.normalizedHeight);
    const weight = weightKg(body.values);
    if (
        body.values.gender == null ||
        body.values.date_of_birth == null ||
        activity == null ||
        goal == null ||
        ageYears(body.values.date_of_birth) == null ||
        height == null ||
        weight == null
    ) {
        return null;
    }
    const rounded = planner.getRoundedResults(
        planner.getGeneralDay({
            settings: {
                variance: GOAL_VARIANCE[goal],
                protein_multiplier: PROTEIN_MULTIPLIER,
                fat_carbs: FAT_CARBS,
            },
            profile: {
                body_fat: null,
                use_kilojoules: false,
                is_imperial: body.values.units === "IMPERIAL",
                activity: ACTIVITY_MULTIPLIER[activity],
                gender: body.values.gender === "MALE" ? "m" : "f",
                date_of_birth: body.values.date_of_birth,
                height: `${height}`,
                weight: `${weight}`,
            },
        }),
        false,
    );
    return {
        calories: parseFloat(rounded.calories_nearest),
        protein: parseFloat(rounded.protein_nearest),
        fat: parseFloat(rounded.fat_nearest),
        carbs: parseFloat(rounded.carbs_nearest),
    };
};

export const toSetup = (
    body: Body,
    activity: Activity | null,
    goal: Goal | null,
    macros: Macros | null,
): server.journal.onboarding.save_macros.input | null => {
    const {values} = body;
    if (
        values.gender == null ||
        values.date_of_birth == null ||
        activity == null ||
        goal == null ||
        !isAboutComplete(body) ||
        !isBodyComplete(body)
    ) {
        return null;
    }
    return {
        profile: {
            date_of_birth: values.date_of_birth,
            gender: values.gender,
            units: values.units,
            feet: values.feet,
            inches: values.inches,
            heightCm: values.heightCm,
            weightLb: values.weightLb,
            weightKg: values.weightKg,
            activity,
            goal,
        },
        targets:
            macros == null
                ? null
                : {
                      calories: `${macros.calories}`,
                      protein: `${macros.protein}`,
                      fat: `${macros.fat}`,
                      carbs: `${macros.carbs}`,
                  },
        today: dates.formatISO(new Date(), {representation: "date"}),
    };
};

const ACTIVITY_FROM_MULTIPLIER: Record<string, Activity> = {
    "1.2": "SEDENTARY",
    "1.375": "ACTIVE",
    "1.55": "VERY_ACTIVE",
    "1.725": "VERY_ACTIVE",
    "1.9": "VERY_ACTIVE",
};

const GOAL_FROM_CODE: Record<number, Goal> = {
    0: "LOSE",
    1: "GAIN",
    2: "MAINTAIN",
};

// The wizard's answers: the shared profile form plus activity and goal.
export const useDetails = (
    existing: server.core.rpc.Profile.output | null,
    demo: boolean,
) => {
    const body = profile.useProfile({
        profile: existing,
        lastWeight: existing?.weight ?? null,
        initial: demo ? DEMO_BODY : undefined,
    });
    const [activity, setActivity] = React.useState<Activity | null>(
        demo
            ? "ACTIVE"
            : existing?.activity == null
              ? null
              : (ACTIVITY_FROM_MULTIPLIER[existing.activity] ?? null),
    );
    const [goal, setGoal] = React.useState<Goal | null>(
        demo
            ? "LOSE"
            : existing?.goal == null
              ? null
              : (GOAL_FROM_CODE[existing.goal] ?? null),
    );
    return {
        body,
        activity,
        setActivity,
        goal,
        setGoal,
        macros: computeMacros(body, activity, goal),
    };
};
